import { useQuery } from '@tanstack/react-query';
import { fetchAdminBreakage } from '../../lib/adminApi';

export default function AdminBreakage() {
  const { data, isLoading } = useQuery({ queryKey: ['admin', 'breakage'], queryFn: fetchAdminBreakage });

  const latest = data?.latest;
  const trend = data?.trend ?? [];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold font-display text-white">Breakage & Economics</h1>

      <div className="bg-slate-800 rounded-xl p-5">
        <p className="text-sm text-slate-400">
          Breakage represents points issued but never redeemed. Higher breakage improves platform margins
          because those points never convert to real cash payouts.
        </p>
      </div>

      {isLoading ? (
        <div className="text-slate-400">Loading...</div>
      ) : latest ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-slate-800 rounded-xl p-5">
              <p className="text-xs text-slate-400 uppercase tracking-wide">Points Issued</p>
              <p className="text-2xl font-bold text-white mt-1 tabular-nums">{latest.total_points_issued.toLocaleString()}</p>
            </div>
            <div className="bg-slate-800 rounded-xl p-5">
              <p className="text-xs text-slate-400 uppercase tracking-wide">Breakage Rate</p>
              <p className="text-2xl font-bold text-amber-400 mt-1 tabular-nums">{(latest.breakage_rate * 100).toFixed(1)}%</p>
            </div>
            <div className="bg-slate-800 rounded-xl p-5">
              <p className="text-xs text-slate-400 uppercase tracking-wide">Liability (USD)</p>
              <p className="text-2xl font-bold text-white mt-1 tabular-nums">${latest.estimated_liability_usd.toFixed(2)}</p>
            </div>
          </div>

          {trend.length > 0 && (
            <div className="bg-slate-800 rounded-xl overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-400 uppercase tracking-wide border-b border-slate-700">
                    <th className="px-4 py-3">Period</th>
                    <th className="px-4 py-3 text-right">Issued</th>
                    <th className="px-4 py-3 text-right">Redeemed</th>
                    <th className="px-4 py-3 text-right">Expired</th>
                    <th className="px-4 py-3 text-right">Outstanding</th>
                    <th className="px-4 py-3 text-right">Breakage %</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {trend.map((row: { cohort_month: string; total_points_issued: number; total_points_redeemed: number; total_points_expired: number; total_points_outstanding: number; breakage_rate: number }, i: number) => (
                    <tr key={i} className="hover:bg-slate-700/50">
                      <td className="px-4 py-3 text-white">{new Date(row.cohort_month).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</td>
                      <td className="px-4 py-3 text-right text-slate-300 tabular-nums">{row.total_points_issued.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right text-slate-300 tabular-nums">{row.total_points_redeemed.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right text-slate-300 tabular-nums">{row.total_points_expired.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right text-slate-300 tabular-nums">{row.total_points_outstanding.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right text-amber-400 tabular-nums">{(row.breakage_rate * 100).toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        <p className="text-slate-500">No breakage data available yet.</p>
      )}
    </div>
  );
}
